import React, { createContext, useContext, useMemo, useState } from "react";

export interface ModelDataset {
  label: string;
  rows: Record<string, any>[];
}

interface Ctx {
  dataset: ModelDataset | null;
  setDataset: (d: ModelDataset | null) => void;
  selectedModelId: string | null;
  setSelectedModelId: (id: string | null) => void;
  /** Bumped whenever a model finishes training so panels refresh their view. */
  lastTrainedAt: number;
  markTrained: (modelId: string) => void;
}

const ModelWorkspaceContext = createContext<Ctx | null>(null);

export const ModelWorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
  const [dataset, setDataset] = useState<ModelDataset | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [lastTrainedAt, setLastTrainedAt] = useState(0);

  const value = useMemo<Ctx>(
    () => ({
      dataset,
      setDataset,
      selectedModelId,
      setSelectedModelId,
      lastTrainedAt,
      markTrained: (modelId: string) => {
        setSelectedModelId(modelId);
        setLastTrainedAt(Date.now());
      },
    }),
    [dataset, selectedModelId, lastTrainedAt],
  );

  return (
    <ModelWorkspaceContext.Provider value={value}>{children}</ModelWorkspaceContext.Provider>
  );
};

export const useModelWorkspace = () => {
  const ctx = useContext(ModelWorkspaceContext);
  if (!ctx) throw new Error("useModelWorkspace must be used inside ModelWorkspaceProvider");
  return ctx;
};
